import moment from 'moment';
import { IScope } from '../app/resource/calendar-item/calendar-item.types';
import { IMeeting } from '../app/resource/meeting/meeting.types';
import { IUser } from '../app/resource/user/user.types';

export const FORMAT_DATE = 'MMM Do, YYYY';
export const FORMAT_DATE_WITHOUT_ORDINALS = 'MMM D, YYYY';
export const FORMAT_DATETIME_WITHOUT_ORDINALS = 'MMM D, YYYY, hh:mm A';
export const SERIALIZE_DATE = 'YYYYMMDD';
export const MONTH_FORMAT = 'MMMM, YYYY';
export const DAY_MONTH_FORMAT = 'MMM Do.';

export const FULL_FORMAT = 'MM/DD/YYYY dddd, hh:mma';
export const HOUR_FORMAT = 'hh:mma';

export const SEARCH_FORMAT = 'YYYY-MM-DD';
export const API_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const DASH_MONTH_FORMAT = 'YYYY-MM';
export const FORMAT_FOR_TIMER = 'MM/DD @ HH:mma';

export const US_FORMAT = 'MMMM DD, YYYY';

export const formatDate = (date: moment.MomentInput): string => moment(date).format(FORMAT_DATE);
export const formatDateWithoutOrdinals = (date: moment.MomentInput): string =>
  moment(date).format(FORMAT_DATE_WITHOUT_ORDINALS);
export const formatDateTimeWithoutOrdinals = (date: moment.MomentInput): string =>
  moment(date).startOf('minute').format(FORMAT_DATETIME_WITHOUT_ORDINALS);
export const formatDateUS = (date: moment.MomentInput): string => moment(date).format(US_FORMAT);
export const serializeDate = (date: moment.MomentInput): string => moment(date).format(SERIALIZE_DATE);
export const formatMonth = (date: moment.MomentInput): string => moment(date).format(MONTH_FORMAT);
export const formatDayMonth = (date: moment.MomentInput): string => moment(date).format(DAY_MONTH_FORMAT);
export const formatHour = (date: moment.MomentInput): string => moment(date).format(HOUR_FORMAT);
export const formatSearch = (date: moment.MomentInput): string => moment(date).format(SEARCH_FORMAT);
export const dashMonth = (date: moment.MomentInput): string => moment(date).format(DASH_MONTH_FORMAT);
export const formatForTimer = (date: moment.MomentInput): string => moment(date).format(FORMAT_FOR_TIMER);

export const formatFull = (date: moment.MomentInput): string => moment(date).format(FULL_FORMAT);

interface IUtcDateRange {
  startDate: moment.MomentInput;
  endDate: moment.MomentInput;
}

export const isUtcDateOnOrAfterToday = (date: moment.MomentInput, todayUtc: moment.Moment = moment.utc()): boolean => {
  return moment.utc(date).isSameOrAfter(todayUtc, 'day');
};

export const isUtcDateWithinRange = (
  date: moment.MomentInput,
  range: IUtcDateRange,
  todayUtc: moment.Moment = moment.utc(),
): boolean => {
  const targetDate = moment.utc(date);
  const startDate = moment.utc(range.startDate);
  const endDate = moment.utc(range.endDate);
  const comparedDay = todayUtc.isValid() ? todayUtc : targetDate;

  return comparedDay.isBetween(startDate, endDate, 'day', '[]');
};

export const filterCurrentAndFutureUtcRanges = <T extends IUtcDateRange>(
  ranges: T[],
  todayUtc: moment.Moment = moment.utc(),
): T[] => {
  return ranges.filter((range) => isUtcDateOnOrAfterToday(range.endDate, todayUtc));
};

export const findCurrentUtcRange = <T extends IUtcDateRange>(
  ranges: T[],
  todayUtc: moment.Moment = moment.utc(),
): T | undefined => {
  return ranges.find((range) => isUtcDateWithinRange(todayUtc, range, todayUtc));
};

export const formatDay = (date: moment.MomentInput): string =>
  ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][moment(date).day()];

export const difference = (begin: moment.MomentInput, end: moment.MomentInput): string => {
  const duration = moment.duration(moment(end).diff(moment(begin)));
  if (duration.asMinutes() < 60) {
    return `${Math.ceil(duration.asMinutes())} ${Math.ceil(duration.asHours()) === 1 ? 'minute' : 'minutes'}`;
  }
  if (duration.asHours() < 24) {
    return `${Math.ceil(duration.asHours())} ${Math.ceil(duration.asHours()) === 1 ? 'hour' : 'hours'}`;
  }
  return `${Math.ceil(duration.asDays())} ${Math.ceil(duration.asDays()) === 1 ? 'day' : 'days'}`;
};

export const hourDuration = (start: moment.MomentInput, end: moment.MomentInput) => {
  const duration = moment.duration(moment(start).diff(end));
  return Math.ceil(duration.asHours());
};

export const generateDatesForScope = (from: moment.MomentInput, scope: IScope) => {
  if (scope === 'month') {
    return Array.from({ length: moment(from).daysInMonth() }, (x, i) =>
      moment(from).startOf('month').add(i, 'days').format('YYYYMMDD'),
    );
  } else if (scope === 'week') {
    return Array.from({ length: 7 }, (x, i) => moment(from).startOf('isoWeek').add(i, 'days').format('YYYYMMDD'));
  }
  return [serializeDate(from)];
};

export const nextDate = (date: moment.MomentInput) => moment(date).add(1, 'day');
export const prevDate = (date: moment.MomentInput) => moment(date).add(-1, 'day');
export const nextWeek = (date: moment.MomentInput) => moment(date).add(1, 'week');
export const prevWeek = (date: moment.MomentInput) => moment(date).add(-1, 'week');
export const nextMonth = (date: moment.MomentInput) => moment(date).add(1, 'month');
export const prevMonth = (date: moment.MomentInput) => moment(date).add(-1, 'month');

export const addHours = (date: moment.MomentInput, hours: number) => moment(date).add(hours, 'hour');

export const getDaysOfMonth = function (yearMonth: string) {
  const daysInMonth = moment(yearMonth, DASH_MONTH_FORMAT).daysInMonth();
  const arrayDays: string[] = [];
  for (let i = 1; i <= daysInMonth; i++) {
    if (i < 10) {
      arrayDays.push(`${yearMonth}-0${i}`);
    } else {
      arrayDays.push(`${yearMonth}-${i}`);
    }
  }
  return arrayDays;
};

export const getDaysOfWeek = (current: string) => generateDatesForScope(current, 'week');

export const calendarWeekFormat = (date: string) => {
  const daysOfWeek = getDaysOfWeek(date);
  return `${formatDate(daysOfWeek[0])} - ${formatDate(daysOfWeek[6])}`;
};

export const getHoursForDate = (date: string) =>
  [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map((hour) => addHours(date, hour));

// const timeToDecimal = (t: string) => {
//   const time = t.slice(1);
//   const arr = time.split(':');
//   const dec = (parseInt(arr[1]) / 6) * 10;

//   return parseFloat(parseInt(arr[0], 10) + '.' + (dec < 10 ? '0' : '') + dec);
// };

export const calculateTimeZoneDateTime = (
  startDate: IMeeting['startDate'],
  startTime: IMeeting['startTime'],
  server: boolean,
) => {
  const meetingDate = `${moment(startDate).format('YYYY-MM-DD')} ${startTime}`;
  const utcMetingDate = moment.utc(meetingDate);
  if (server) {
    const serverDateTime = moment(meetingDate).utc().format('YYYY-MM-DD HH:mm');

    return serverDateTime;
  }
  return moment(utcMetingDate).local().format('YYYY-MM-DD HH:mm');
};

/**
 * Parses a timezone in a format "(GMT-8:00) Pacific Time (US and Canada)"
 * and returns the offset in minutes.
 * If not in that format, then return null.
 * @param tz Timezone
 * @returns Offset in minutes
 */
function extractOffsetTZ(tz: string): number | null {
  const offsetMatch = tz.match(/\(GMT([+-]\d{1,2}:\d{2})\)/);

  if (offsetMatch && offsetMatch[1]) {
    const parts = offsetMatch[1].split(':');
    const sign = parts[0].startsWith('-') ? -1 : 1;
    const hours = Math.abs(parseInt(parts[0], 10));
    const minutes = parseInt(parts[1], 10);
    return sign * (hours * 60 + minutes);
  }

  return null;
}

/**
 * Converts date from UTC -> to User TZ
 * @param date
 * @param userTimezone
 * @param format
 * @returns
 */
export const convertDateToUserTimeZone = (date: string, userTimezone: IUser['timezone']) => {
  // initialize as it comes
  let dateInUserTimeZone = moment.utc(date, 'YYYY-MM-DD HH:mm');

  if (date && userTimezone) {
    const offset = extractOffsetTZ(userTimezone);

    if (offset) {
      dateInUserTimeZone = dateInUserTimeZone.utcOffset(offset);
    } else {
      dateInUserTimeZone = dateInUserTimeZone.tz(userTimezone);
    }
  }
  return dateInUserTimeZone;
};

/**
 * Checks if last session date is within 12 hours before now or next session date is within 12 hours after now.
 * This is in order to create a new custom session or use one of the already generated.
 *
 * @param lastSessionDate string
 * @param nextSessionDate string
 * @returns boolean
 */
export const isSessionToday = (lastSessionDate: string, nextSessionDate: string) => {
  const startDatetime = moment.utc().subtract(12, 'h');
  const endDatetime = moment.utc().add(12, 'h');

  const lastSessionDatetime = moment.utc(lastSessionDate);
  const nextSessionDatetime = moment.utc(nextSessionDate);

  return lastSessionDatetime.isAfter(startDatetime) || nextSessionDatetime.isBefore(endDatetime);
};

export const isNextSessionWithinTheNextTwelveHours = (nextSessionDate: string) => {
  const now = moment.utc();
  const twelveHoursAfterNow = moment.utc().add(12, 'h');

  const sessionDatetime = moment.utc(nextSessionDate);

  return sessionDatetime.isBetween(now, twelveHoursAfterNow);
};

/**
 * Converts date from UTC -> to User TZ and format
 * @param date
 * @param userTimezone
 * @param format
 * @returns
 */
export const convertAndFormatDateToUserTimeZone = (
  date: string,
  userTimezone: IUser['timezone'],
  format = 'MMMM DD, YYYY, hh:mm A',
) => {
  const dateInUserTimeZone = convertDateToUserTimeZone(date, userTimezone);

  return dateInUserTimeZone.format(format);
};

export const formatUtcDateToUserTimeZone = (
  date: string | null | undefined,
  userTimezone: IUser['timezone'],
  format = FORMAT_DATE_WITHOUT_ORDINALS,
): string | null => {
  if (!date) {
    return null;
  }

  const formatted = convertAndFormatDateToUserTimeZone(date, userTimezone, format);
  if (!formatted || formatted === 'Invalid date') {
    return date;
  }

  return formatted;
};

export const formatUtcNowForApi = (): string => moment.utc().format(API_DATETIME_FORMAT);

/**
 * Converts date from User TZ -> to UTC
 * @param date
 * @param userTimezone
 * @param format
 * @returns
 */
export const convertDateFromUserTimeZone = (date: string, userTimezone: IUser['timezone']) => {
  if (!date) {
    throw new Error('Date is required.');
  }

  let dateInUserTimeZone = moment(date, 'YYYY-MM-DD HH:mm');
  let dateInUtc = dateInUserTimeZone;
  if (userTimezone) {
    const offset = extractOffsetTZ(userTimezone);
    if (offset) {
      dateInUserTimeZone = dateInUserTimeZone.utcOffset(offset, true);
    } else {
      dateInUserTimeZone = moment.tz(date, 'YYYY-MM-DD HH:mm', userTimezone);
    }
    dateInUtc = dateInUserTimeZone.utc();
  }

  return dateInUtc;
};

export function convertDateAndTimeFromUTC(
  date: string,
  time: string,
  userTimezone: string,
  options: { timeLeadingZeo: boolean } = { timeLeadingZeo: true },
) {
  const dateStr = convertAndFormatDateToUserTimeZone(`${date} ${time}`, userTimezone, 'YYYY-MM-DD');

  let timeFormat = 'HH:mm';
  if (!options.timeLeadingZeo) {
    timeFormat = 'H:mm';
  }
  const timeStr = convertAndFormatDateToUserTimeZone(`${date} ${time}`, userTimezone, timeFormat);

  return {
    date: dateStr,
    time: timeStr,
  };
}

export function convertDateAndTimeToUTC(date: string, time: string, userTimezone: string | null) {
  const convertedDate = convertDateFromUserTimeZone(`${date} ${time}`, userTimezone);
  const dateStr = convertedDate.format('YYYY-MM-DD');
  const timeStr = convertedDate.format('HH:mm');

  return {
    date: dateStr,
    time: timeStr,
  };
}

export function addSecondsAndFormatTime(timestamp: string, secondsString: string, timezone: string) {
  const convertedDatetime = convertDateToUserTimeZone(timestamp, timezone);
  return convertedDatetime.add(parseFloat(secondsString), 'seconds').format('h:mmA').toLowerCase();
}

/**
 * Get the timezones that are valid also for PHP
 * @returns
 */
export function getValidTimezones() {
  // Code using moment
  // let timezones = moment.tz.names();

  // timezones = timezones.filter(
  //   (tz) =>
  //     // filter out the obsolete timezones
  //     ![
  //       'America/Buenos_Aires',
  //       'America/Catamarca',
  //       'America/Argentina/ComodRivadavia',
  //       'America/Atka',
  //       'America/Coral_Harbour',
  //       'America/Cordoba',
  //       'America/Ensenada',
  //       'America/Fort_Wayne',
  //       'America/Godthab',
  //       'America/Indianapolis',
  //       'America/Knox_IN',
  //       'America/Louisville',
  //       'America/Mendoza',
  //       'America/Montreal',
  //       'America/Nipigon',
  //     ].includes(tz),
  // );

  const timezones = [
    {
      label: '(GMT-11:00) Midway Island, Samoa',
      value: 'Pacific/Midway',
    },
    {
      label: '(GMT-11:00) Pago Pago',
      value: 'Pacific/Pago_Pago',
    },
    {
      label: '(GMT-10:00) Hawaii',
      value: 'Pacific/Honolulu',
    },
    {
      label: '(GMT-09:00) Alaska',
      value: 'America/Anchorage',
    },
    {
      label: '(GMT-09:00) Juneau',
      value: 'America/Juneau',
    },
    {
      label: '(GMT-08:00) Vancouver',
      value: 'America/Vancouver',
    },
    {
      label: '(GMT-08:00) Pacific Time (US and Canada)',
      value: 'America/Los_Angeles',
    },
    {
      label: '(GMT-08:00) Tijuana',
      value: 'America/Tijuana',
    },
    {
      label: '(GMT-07:00) Edmonton',
      value: 'America/Edmonton',
    },
    {
      label: '(GMT-07:00) Mountain Time (US and Canada)',
      value: 'America/Denver',
    },
    {
      label: '(GMT-07:00) Arizona',
      value: 'America/Phoenix',
    },
    {
      label: '(GMT-07:00) Mazatlan',
      value: 'America/Mazatlan',
    },
    {
      label: '(GMT-07:00) Chihuahua',
      value: 'America/Chihuahua',
    },
    {
      label: '(GMT-07:00) Yukon',
      value: 'America/Whitehorse',
    },
    {
      label: '(GMT-06:00) Winnipeg',
      value: 'America/Winnipeg',
    },
    {
      label: '(GMT-06:00) Saskatchewan',
      value: 'America/Regina',
    },
    {
      label: '(GMT-06:00) Central Time (US and Canada)',
      value: 'America/Chicago',
    },
    {
      label: '(GMT-06:00) Mexico City',
      value: 'America/Mexico_City',
    },
    {
      label: '(GMT-06:00) Guatemala',
      value: 'America/Guatemala',
    },
    {
      label: '(GMT-06:00) El Salvador',
      value: 'America/El_Salvador',
    },
    {
      label: '(GMT-06:00) Managua',
      value: 'America/Managua',
    },
    {
      label: '(GMT-06:00) Costa Rica',
      value: 'America/Costa_Rica',
    },
    {
      label: '(GMT-06:00) Tegucigalpa',
      value: 'America/Tegucigalpa',
    },
    {
      label: '(GMT-06:00) Monterrey',
      value: 'America/Monterrey',
    },
    {
      label: '(GMT-05:00) Montreal',
      value: 'America/Montreal',
    },
    {
      label: '(GMT-05:00) Eastern Time (US and Canada)',
      value: 'America/New_York',
    },
    {
      label: '(GMT-05:00) Indiana (East)',
      value: 'America/Indiana/Indianapolis',
    },
    {
      label: '(GMT-05:00) Panama',
      value: 'America/Panama',
    },
    {
      label: '(GMT-05:00) Bogota',
      value: 'America/Bogota',
    },
    {
      label: '(GMT-05:00) Lima',
      value: 'America/Lima',
    },
    {
      label: '(GMT-05:00) Acre',
      value: 'America/Rio_Branco',
    },
    {
      label: '(GMT-04:00) Halifax',
      value: 'America/Halifax',
    },
    {
      label: '(GMT-04:00) Puerto Rico',
      value: 'America/Puerto_Rico',
    },
    {
      label: '(GMT-04:00) Caracas',
      value: 'America/Caracas',
    },
    {
      label: '(GMT-04:00) Atlantic Time (Canada)',
      value: 'America/Halifax',
    },
    {
      label: '(GMT-04:00) La Paz',
      value: 'America/La_Paz',
    },
    {
      label: '(GMT-04:00) Guyana',
      value: 'America/Guyana',
    },
    {
      label: '(GMT-03:30) Newfoundland and Labrador',
      value: 'America/St_Johns',
    },
    {
      label: '(GMT-03:00) Santiago',
      value: 'America/Santiago',
    },
    {
      label: '(GMT-03:00) Montevideo',
      value: 'America/Montevideo',
    },
    {
      label: '(GMT-03:00) Recife',
      value: 'America/Recife',
    },
    {
      label: '(GMT-03:00) Buenos Aires, Georgetown',
      value: 'America/Argentina/Buenos_Aires',
    },
    {
      label: '(GMT-03:00) Greenland',
      value: 'America/Godthab',
    },
    {
      label: '(GMT-03:00) Sao Paulo',
      value: 'America/Sao_Paulo',
    },
    {
      label: '(GMT-02:00) Fernando de Noronha',
      value: 'America/Noronha',
    },
    {
      label: '(GMT-01:00) Azores',
      value: 'Atlantic/Azores',
    },
    {
      label: '(GMT-01:00) Cape Verde Islands',
      value: 'Atlantic/Cape_Verde',
    },
    {
      label: '(GMT+00:00) Universal Time UTC',
      value: 'UTC',
    },
    {
      label: '(GMT+00:00) Greenwich Mean Time',
      value: 'GMT',
    },
    {
      label: '(GMT+00:00) Reykjavik',
      value: 'Atlantic/Reykjavik',
    },
    {
      label: '(GMT+00:00) Dublin',
      value: 'Europe/Dublin',
    },
    {
      label: '(GMT+00:00) London',
      value: 'Europe/London',
    },
    {
      label: '(GMT+00:00) Lisbon',
      value: 'Europe/Lisbon',
    },
    {
      label: '(GMT+00:00) Nouakchott',
      value: 'Africa/Nouakchott',
    },
    {
      label: '(GMT+01:00) Belgrade, Bratislava, Ljubljana',
      value: 'Europe/Belgrade',
    },
    {
      label: '(GMT+01:00) Sarajevo, Skopje, Zagreb',
      value: 'Europe/Sarajevo',
    },
    {
      label: '(GMT+01:00) Casablanca',
      value: 'Africa/Casablanca',
    },
    {
      label: '(GMT+01:00) Oslo',
      value: 'Europe/Oslo',
    },
    {
      label: '(GMT+01:00) Copenhagen',
      value: 'Europe/Copenhagen',
    },
    {
      label: '(GMT+01:00) Brussels',
      value: 'Europe/Brussels',
    },
    {
      label: '(GMT+01:00) Amsterdam, Berlin, Rome, Stockholm, Vienna',
      value: 'Europe/Amsterdam',
    },
    {
      label: '(GMT+01:00) Amsterdam',
      value: 'Europe/Amsterdam',
    },
    {
      label: '(GMT+01:00) Rome',
      value: 'Europe/Rome',
    },
    {
      label: '(GMT+01:00) Stockholm',
      value: 'Europe/Stockholm',
    },
    {
      label: '(GMT+01:00) Vienna',
      value: 'Europe/Vienna',
    },
    {
      label: '(GMT+01:00) Luxembourg',
      value: 'Europe/Luxembourg',
    },
    {
      label: '(GMT+01:00) Paris',
      value: 'Europe/Paris',
    },
    {
      label: '(GMT+01:00) Zurich',
      value: 'Europe/Zurich',
    },
    {
      label: '(GMT+01:00) Madrid',
      value: 'Europe/Madrid',
    },
    {
      label: '(GMT+01:00) West Central Africa',
      value: 'Africa/Lagos',
    },
    {
      label: '(GMT+01:00) Algiers',
      value: 'Africa/Algiers',
    },
    {
      label: '(GMT+01:00) Tunis',
      value: 'Africa/Tunis',
    },
    { label: '(GMT+01:00) Warsaw', value: 'Europe/Warsaw' },
    { label: '(GMT+01:00) Prague Bratislava', value: 'Europe/Prague' },
    { label: '(GMT+01:00) Budapest', value: 'Europe/Budapest' },
    {
      label: '(GMT+02:00) Helsinki',
      value: 'Europe/Helsinki',
    },
    {
      label: '(GMT+02:00) Harare, Pretoria',
      value: 'Africa/Harare',
    },
    {
      label: '(GMT+02:00) Sofia',
      value: 'Europe/Sofia',
    },
    {
      label: '(GMT+02:00) Athens',
      value: 'Europe/Athens',
    },
    {
      label: '(GMT+02:00) Bucharest',
      value: 'Europe/Bucharest',
    },
    {
      label: '(GMT+02:00) Nicosia',
      value: 'Asia/Nicosia',
    },
    {
      label: '(GMT+02:00) Beirut',
      value: 'Asia/Beirut',
    },
    {
      label: '(GMT+02:00) Damascus',
      value: 'Asia/Damascus',
    },
    {
      label: '(GMT+02:00) Jerusalem',
      value: 'Asia/Jerusalem',
    },
    {
      label: '(GMT+02:00) Amman',
      value: 'Asia/Amman',
    },
    {
      label: '(GMT+02:00) Tripoli',
      value: 'Africa/Tripoli',
    },
    {
      label: '(GMT+02:00) Cairo',
      value: 'Africa/Cairo',
    },
    {
      label: '(GMT+02:00) Johannesburg',
      value: 'Africa/Johannesburg',
    },
    {
      label: '(GMT+02:00) Khartoum',
      value: 'Africa/Khartoum',
    },
    {
      label: '(GMT+02:00) Kiev',
      value: 'Europe/Kiev',
    },
    {
      label: '(GMT+02:00) Chisinau',
      value: 'Europe/Chisinau',
    },
    { label: '(GMT+03:00) Nairobi', value: 'Africa/Nairobi' },
    { label: '(GMT+03:00) Istanbul', value: 'Europe/Istanbul' },
    { label: '(GMT+03:00) Moscow', value: 'Europe/Moscow' },
    { label: '(GMT+03:00) Baghdad', value: 'Asia/Baghdad' },
    { label: '(GMT+03:00) Kuwait', value: 'Asia/Kuwait' },
    { label: '(GMT+03:00) Riyadh', value: 'Asia/Riyadh' },
    { label: '(GMT+03:00) Bahrain', value: 'Asia/Bahrain' },
    { label: '(GMT+03:00) Qatar', value: 'Asia/Qatar' },
    { label: '(GMT+03:00) Aden', value: 'Asia/Aden' },
    { label: '(GMT+03:00) Djibouti', value: 'Africa/Djibouti' },
    { label: '(GMT+03:00) Mogadishu', value: 'Africa/Mogadishu' },
    { label: '(GMT+03:00) Minsk', value: 'Europe/Minsk' },
    { label: '(GMT+03:30) Tehran', value: 'Asia/Tehran' },
    { label: '(GMT+04:00) Dubai', value: 'Asia/Dubai' },
    { label: '(GMT+04:00) Muscat', value: 'Asia/Muscat' },
    { label: '(GMT+04:00) Baku, Tbilisi, Yerevan', value: 'Asia/Baku' },
    { label: '(GMT+04:30) Kabul', value: 'Asia/Kabul' },
    {
      label: '(GMT+05:00) Yekaterinburg',
      value: 'Asia/Yekaterinburg',
    },
    {
      label: '(GMT+05:00) Islamabad, Karachi, Tashkent',
      value: 'Asia/Karachi',
    },
    {
      label: '(GMT+05:30) India',
      value: 'Asia/Calcutta',
    },
    {
      label: '(GMT+05:30) Mumbai, Kolkata, New Delhi',
      value: 'Asia/Kolkata',
    },
    {
      label: '(GMT+05:30) Colombo',
      value: 'Asia/Colombo',
    },
    {
      label: '(GMT+05:45) Kathmandu',
      value: 'Asia/Kathmandu',
    },
    {
      label: '(GMT+06:00) Almaty',
      value: 'Asia/Almaty',
    },
    {
      label: '(GMT+06:00) Astana, Dhaka',
      value: 'Asia/Almaty',
    },
    {
      label: '(GMT+06:30) Rangoon',
      value: 'Asia/Yangon',
    },
    {
      label: '(GMT+07:00) Novosibirsk',
      value: 'Asia/Novosibirsk',
    },
    {
      label: '(GMT+07:00) Krasnoyarsk',
      value: 'Asia/Krasnoyarsk',
    },
    {
      label: '(GMT+07:00) Bangkok',
      value: 'Asia/Bangkok',
    },
    {
      label: '(GMT+07:00) Vietnam',
      value: 'Asia/Ho_Chi_Minh',
    },
    {
      label: '(GMT+07:00) Jakarta',
      value: 'Asia/Jakarta',
    },
    {
      label: '(GMT+08:00) Irkutsk, Ulaanbaatar',
      value: 'Asia/Irkutsk',
    },
    {
      label: '(GMT+08:00) Beijing, Shanghai',
      value: 'Asia/Shanghai',
    },
    {
      label: '(GMT+08:00) Hong Kong SAR',
      value: 'Asia/Hong_Kong',
    },
    {
      label: '(GMT+08:00) Taipei',
      value: 'Asia/Taipei',
    },
    {
      label: '(GMT+08:00) Kuala Lumpur',
      value: 'Asia/Kuala_Lumpur',
    },
    {
      label: '(GMT+08:00) Singapore',
      value: 'Asia/Singapore',
    },
    {
      label: '(GMT+08:00) Perth',
      value: 'Australia/Perth',
    },
    {
      label: '(GMT+09:00) Yakutsk',
      value: 'Asia/Yakutsk',
    },
    {
      label: '(GMT+09:00) Seoul',
      value: 'Asia/Seoul',
    },
    {
      label: '(GMT+09:00) Osaka, Sapporo, Tokyo',
      value: 'Asia/Tokyo',
    },
    {
      label: '(GMT+09:30) Darwin',
      value: 'Australia/Darwin',
    },
    {
      label: '(GMT+10:00) Vladivostok',
      value: 'Asia/Vladivostok',
    },
    {
      label: '(GMT+10:00) Guam, Port Moresby',
      value: 'Pacific/Guam',
    },
    {
      label: '(GMT+10:00) Brisbane',
      value: 'Australia/Brisbane',
    },
    {
      label: '(GMT+10:30) Adelaide',
      value: 'Australia/Adelaide',
    },
    {
      label: '(GMT+11:00) Canberra, Melbourne, Sydney',
      value: 'Australia/Sydney',
    },
    {
      label: '(GMT+11:00) Hobart',
      value: 'Australia/Hobart',
    },
    {
      label: '(GMT+11:00) Magadan',
      value: 'Asia/Magadan',
    },
    {
      label: '(GMT+11:00) Solomon Islands',
      value: 'Pacific/Guadalcanal',
    },
    {
      label: '(GMT+11:00) New Caledonia',
      value: 'Pacific/Noumea',
    },
    {
      label: '(GMT+11:00) Lord Howe IsIand',
      value: 'Australia/Lord_Howe',
    },
    {
      label: '(GMT+12:00) Kamchatka',
      value: 'Asia/Kamchatka',
    },
    {
      label: '(GMT+12:00) Fiji Islands, Marshall Islands',
      value: 'Pacific/Fiji',
    },
    {
      label: '(GMT+13:00) Auckland, Wellington',
      value: 'Pacific/Auckland',
    },
    {
      label: '(GMT+13:00) Independent State of Samoa',
      value: 'Pacific/Apia',
    },
  ];

  return timezones;
}

/**
 * Guess the user timezone and make sure it's one of the valid ones
 */
export function getUserBrowserTimezoneName() {
  let guessedTimezoneName = moment.tz.guess();

  const validTimezones = getValidTimezones();
  const tzIndex = validTimezones.findIndex((tz) => tz.value === guessedTimezoneName);
  if (tzIndex === -1) {
    console.warn(`The guessed timezone "${guessedTimezoneName}" is not standard.`);
    // if not found then return a hardcoded one, for now
    // @TODO: improve/fix this and do something better
    guessedTimezoneName = 'America/Los_Angeles';
  }

  console.log('guessedTimezoneName', guessedTimezoneName);
  return guessedTimezoneName;
}
